import { ReactNode, createContext, useEffect, useReducer } from "react";

// eslint-disable-next-line import/no-named-as-default
import produce from "immer";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";

import {
  DASHBOARD_SCREENSHOT_MODE_BODY_CLASS,
  SCREENSHOT_MODE_BODY_CLASS
} from "../constants";
import {
  ScreenshotContextState,
  UpdateIsChangingPreset,
  UpdateIsResettingPreset,
  UpdateLoadingState,
  UpdateScreenshotSettingsState,
  UpdateWidget,
  UpdateWidgetScreenshotOverlay,
  UpdateWidgetState
} from "./ScreenshotContextState";

export const initialScreenshotContextState: ScreenshotContextState = {
  isLoading: false,
  isChangingPreset: false,
  isResettingPreset: false
};

export const ScreenshotContext = createContext(initialScreenshotContextState);

export interface Action<T, Type> {
  type?: Type;
  payload: T;
}
export const ScreenshotDispatchContext =
  createContext<React.Dispatch<Action<ScreenshotUpdates, "update">>>(undefined);

export type ScreenshotUpdates =
  | UpdateIsChangingPreset
  | UpdateLoadingState
  | UpdateIsResettingPreset
  | UpdateScreenshotSettingsState
  | UpdateWidget
  | UpdateWidgetState
  | UpdateWidgetScreenshotOverlay;

function ScreenshotReducer(
  state: ScreenshotContextState,
  action: Action<ScreenshotUpdates, "update">
) {
  return produce(state, (draft) => {
    Object.assign(draft, action.payload);
  });
}

export interface ScreenshotProviderModel {
  children: ReactNode;
  state?: ScreenshotContextState;
  useWrapper?: boolean;
}

/**
 * Clear screenshot state from all chart sessionStorage settings.
 * This runs synchronously during module load and again on first render
 * to ensure charts don't restore stale screenshot state after page reload.
 *
 * Chart settings can have screenshot state in different paths:
 * - Old charts: settings.screenshot (boolean) or settings.screenshot.visible
 * - New charts: settings.features.screenshot.active
 */
function clearScreenshotFromSessionStorage() {
  // Include all chart-like widget types that might have screenshot state persisted
  const chartPrefixes = [
    "chart::",
    "chart-old::",
    "midstream-chart::",
    "midstream-chart-old::",
    "multiphase-chart::",
    "sam::",
    "sam-rf-pv::",
    // The lean widgets no longer WRITE these flags (see TRANSIENT_FEATURES in
    // useChartWidgetSession), so going forward there is nothing here to clear.
    // Listed anyway to sweep records written before that change: a dashboard saved
    // mid-capture already holds `screenshot.active`, and without a sweep it reopens
    // stuck for as long as that record survives.
    "rigfrac-chart::",
    "wellstatus-chart::"
  ];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && chartPrefixes.some((prefix) => key.startsWith(prefix))) {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const settings = JSON.parse(stored);
          let modified = false;

          // Check old chart format: settings.screenshot (boolean or object with visible)
          if (settings?.screenshot === true) {
            settings.screenshot = false;
            modified = true;
          }
          if (settings?.screenshot?.visible) {
            settings.screenshot.visible = false;
            modified = true;
          }
          if (settings?.screenshot?.active) {
            settings.screenshot.active = false;
            modified = true;
          }

          // Check new chart format: settings.features.screenshot.active
          if (settings?.features?.screenshot?.active) {
            settings.features.screenshot.active = false;
            modified = true;
          }
          if (settings?.features?.screenshot?.visible) {
            settings.features.screenshot.visible = false;
            modified = true;
          }

          // Screenshot mode also sets fullScreen.active, clear that too
          if (settings?.features?.fullScreen?.active) {
            settings.features.fullScreen.active = false;
            modified = true;
          }

          if (modified) {
            sessionStorage.setItem(key, JSON.stringify(settings));
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }
  }
}

// Run cleanup at module load time (before any React components render)
clearScreenshotFromSessionStorage();

export function ScreenshotProvider({
  children,
  state: overrideState
}: ScreenshotProviderModel) {
  const [state, dispatch] = useReducer(
    ScreenshotReducer,
    overrideState ?? initialScreenshotContextState
  );

  // Reset dashboard screenshot store on mount to ensure screenshot mode doesn't
  // persist across page reloads or HMR. The Zustand store is created at module
  // level, so its state can survive HMR. This effect ensures a clean slate.
  // Also remove the screenshot-mode CSS classes from body in case they persisted.
  useEffect(() => {
    useDashboardScreenshotStore.getState().deactivate();
    document.body.classList.remove(
      SCREENSHOT_MODE_BODY_CLASS,
      DASHBOARD_SCREENSHOT_MODE_BODY_CLASS
    );
    // Run again in case HMR reloaded the module but not this component
    clearScreenshotFromSessionStorage();
  }, []);

  return (
    <ScreenshotContext.Provider value={state}>
      <ScreenshotDispatchContext.Provider value={dispatch}>
        {children}
      </ScreenshotDispatchContext.Provider>
    </ScreenshotContext.Provider>
  );
}
